/**
 * `usePortEventsStream` — subscribes to the `/sse/vessel/:mmsi/port-events`
 * stream and appends incoming port-event frames to a local state array.
 *
 * Live events arrive with type `port-event`. The hook exposes:
 *   - `events`: current ordered list of port events (oldest → newest).
 *   - `status`: connection lifecycle (connecting / open / reconnecting / paused).
 *   - `retry`: force-reconnect function (wired to "Retry now" button).
 *
 * Reconnection UX: when status is "reconnecting", the caller renders a
 * small "Reconnecting…" badge with `Loader2 animate-spin` (per uncodixify-ui).
 */

import { useState, useCallback, useMemo } from "react";
import { useSSE, type SSEStatus } from "./useSSE.js";

export interface PortEventFrame {
  id: number;
  vesselId: number | null;
  mmsi: string | null;
  portName: string;
  countryCode: string | null;
  lat: number;
  lng: number;
  eventType: string;
  occurredAt: string; // ISO-8601
  notes: string | null;
  sourceUrl: string;
  verifiedAt: string; // ISO-8601
}

export interface UsePortEventsStreamOptions {
  /** When false, the SSE connection is not opened. */
  enabled?: boolean;
  /** Override the API base URL — used by tests. */
  apiBaseUrl?: string;
}

export interface UsePortEventsStreamResult {
  events: PortEventFrame[];
  status: SSEStatus;
  reconnecting: boolean;
  retry: () => void;
}

export function usePortEventsStream(
  vesselMmsi: string | null | undefined,
  initialEvents: PortEventFrame[] = [],
  options: UsePortEventsStreamOptions = {},
): UsePortEventsStreamResult {
  const { enabled = true, apiBaseUrl = "" } = options;

  const [events, setEvents] = useState<PortEventFrame[]>(initialEvents);

  const handlePortEvent = useCallback((data: PortEventFrame) => {
    setEvents((prev) => {
      // Deduplicate by id; append new events, keep sorted by occurredAt.
      if (prev.some((e) => e.id === data.id)) return prev;
      const next = [...prev, data].sort(
        (a, b) =>
          new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
      );
      return next;
    });
  }, []);

  const url =
    vesselMmsi && enabled
      ? `${apiBaseUrl}/sse/vessel/${vesselMmsi}/port-events`
      : "";

  const { status, reconnecting, retry } = useSSE<"port-event", PortEventFrame>(
    url,
    useMemo(() => ({ "port-event": handlePortEvent }), [handlePortEvent]),
    { enabled: Boolean(vesselMmsi && enabled) },
  );

  return { events, status, reconnecting, retry };
}
